"use client";

import type React from "react";
import { createContext, useState, useContext, useEffect } from "react";
import { useAuth } from './AuthContext';
import { buildApiUrl } from '../config/api';

type Theme = "light" | "dark";

type ThemeContextType = {
  theme: Theme;
  toggleTheme: () => void;
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [theme, setTheme] = useState<Theme>("light");
  const [isInitialized, setIsInitialized] = useState(false);
  const { isAuthenticated, token } = useAuth();

  // Load theme from DB or localStorage
  useEffect(() => {
    const loadTheme = async () => {
      try {
        
        // First, load from localStorage immediately for faster rendering
        const savedTheme = localStorage.getItem("theme") as Theme | null;
        
        if (savedTheme) {
          setTheme(savedTheme);
          // Apply immediately to prevent flash
          if (savedTheme === "dark") {
            document.documentElement.classList.add("dark");
          } else {
            document.documentElement.classList.remove("dark");
          }
        }

        // If authenticated, fetch from DB but prioritize localStorage
        if (isAuthenticated && token) {
          const response = await fetch(buildApiUrl('/api/settings/darkMode'), {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });

          if (response.ok) {
            const data = await response.json();
            const darkMode = data.data?.darkMode === 'true' || data.data?.darkMode === true;
            const dbTheme = darkMode ? "dark" : "light";
            
            // If we have localStorage theme, prioritize it and sync to DB
            if (savedTheme && savedTheme !== dbTheme) {
              // Update DB with localStorage value
              fetch(buildApiUrl('/api/settings/darkMode'), {
                method: 'PUT',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({ value: savedTheme === "dark" })
              }).catch(error => {
                console.error('Error syncing theme to DB:', error);
              });
            } else if (!savedTheme) {
              // Only use DB theme if we don't have localStorage
              setTheme(dbTheme);
            }
            setIsInitialized(true);
            return;
          }
        }

        // Use localStorage theme or default to light
        const initialTheme = savedTheme || "light";
        setTheme(initialTheme);
        setIsInitialized(true);
      } catch (error) {
        console.error('Error loading theme:', error);
        // Fallback to localStorage
        const savedTheme = localStorage.getItem("theme") as Theme | null;
        setTheme(savedTheme || "light");
        setIsInitialized(true);
      }
    };

    loadTheme();
  }, [isAuthenticated, token]);

  // Apply theme to DOM and save
  useEffect(() => {
    if (isInitialized) {
      
      // Update DOM
      if (theme === "dark") {
        document.documentElement.classList.add("dark");
      } else {
        document.documentElement.classList.remove("dark");
      }
      
      // Save to localStorage for immediate effect
      localStorage.setItem("theme", theme);
      
      // Save to DB if authenticated
      if (isAuthenticated && token) {
        fetch(buildApiUrl('/api/settings/darkMode'), {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ value: theme === "dark" })
        }).then(response => {
        }).catch(error => {
          console.error('Error saving theme to DB:', error);
        });
      }
    }
  }, [theme, isInitialized, isAuthenticated, token]);

  const toggleTheme = () => {
    setTheme((prevTheme) => {
      const newTheme = prevTheme === "light" ? "dark" : "light";
      return newTheme;
    });
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};
